import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { User, OutstandingReportData } from 'shared/types.js';
import { api } from './api.js';
import { currencyFormatter } from './utils.js';

const BarChart = ({ data, dataKey, labelKey, title, formatter }: { 
    data: any[], 
    dataKey: string, 
    labelKey: string, 
    title: string,
    formatter: (value: number) => string
}) => {
    const maxValue = useMemo(() => {
        if (!data || data.length === 0) return 0;
        return Math.max(...data.map(d => d[dataKey]));
    }, [data, dataKey]);
    const getStatusColor = (status: string | null) => {
        switch (status) {
            case 'new': return '#fa5252';
            case 'partially_cleared': return '#fcc419';
            case 'cleared_with_issues': return '#ae3ec9';
            case 'fully_cleared': return '#4c6ef5';
            case null: return '#868e96';
            default: return 'var(--primary-color)';
        }
    };
    return (
        <div className="chart-card">
            <h3>{title}</h3>
            {(!data || data.length === 0) ? (
                <div className="bar-chart-no-data">No data for this period.</div>
            ) : (
                <div className="bar-chart">
                    {data.map((item, index) => {
                        const value = item[dataKey];
                        const originalStatus = item[labelKey];
                        const label = (originalStatus === null ? 'Viewed' : originalStatus)
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, (l: string) => l.toUpperCase());
                        
                        const width = maxValue > 0 ? (value / maxValue) * 100 : 0;
                        const formattedValue = formatter(value);
                        const barColor = title.toLowerCase().includes('status') ? getStatusColor(originalStatus) : 'var(--primary-color)';
                        return (
                            <div key={index} className="bar-chart-row" title={`${label}: ${formattedValue}`}>
                                <div className="bar-chart-label" title={label}>{label}</div>
                                <div className="bar-container">
                                    <div 
                                        className="bar" 
                                        style={{ width: `${width}%`, backgroundColor: barColor }}
                                    >
                                        {width > 30 && formattedValue}
                                    </div>
                                    {width <= 30 && <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: 'var(--text-color)' }}>{formattedValue}</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const KpiCard = ({ title, value, formatter }: { title: string; value: number; formatter?: (value: number) => string | number; }) => (
    <div className="kpi-card">
        <div className="kpi-card-title">{title}</div>
        <div className="kpi-card-value">{formatter ? formatter(value) : value}</div>
    </div>
);

export const Reports = ({ user }: { user: User }) => {
    const statusOptions = [
        { value: 'new', label: 'New' },
        { value: 'partially_cleared', label: 'Partially Cleared' },
        { value: 'cleared_with_issues', label: 'Cleared With Issues' },
        { value: 'fully_cleared', label: 'Cleared' },
    ] as const;
    const getYearToDateRange = () => {
        const today = new Date();
        const start = new Date(today.getFullYear(), 0, 1);
        return {
            startDate: start.toISOString().split('T')[0],
            endDate: today.toISOString().split('T')[0],
        };
    };
    
    const [reportData, setReportData] = useState<OutstandingReportData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [clerkPoRanges, setClerkPoRanges] = useState<Record<string, number[]>>({});
    
    const allowedClerks = useMemo(() => {
        if (user.permissions && 'allowed_clerks' in user.permissions) {
            return Array.isArray(user.permissions.allowed_clerks) ? user.permissions.allowed_clerks : [];
        }
        return null;
    }, [user.permissions]);

    const getDefaultClerk = () => {
        const defaultClerk = user.permissions?.default_clerk;
        if (defaultClerk === 'Unknown' && user.permissions?.can_view_unknown_clerk !== 'true') {
            return 'All';
        }
        if (defaultClerk && (allowedClerks === null || allowedClerks.includes(defaultClerk))) {
            return defaultClerk;
        }
        return 'All';
    };

    const [filters, setFilters] = useState({
        ...getYearToDateRange(),
        selectedClerk: getDefaultClerk(),
        selectedStatuses: new Set(['new', 'viewed', 'partially_cleared', 'cleared_with_issues', 'fully_cleared']),
    });
    const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
    const statusMenuRef = useRef<HTMLDivElement | null>(null);

    const clerksForFilter = useMemo(() => {
        if (allowedClerks !== null) {
            return [...allowedClerks].sort();
        }
        return Object.keys(clerkPoRanges).sort();
    }, [clerkPoRanges, allowedClerks]);

    const averageAmountByClerk = useMemo(() => {
        if (!reportData?.amountByClerk) return [];
        return reportData.amountByClerk.map(clerkData => ({
            ...clerkData,
            averageAmount: clerkData.count > 0 ? clerkData.amount / clerkData.count : 0,
        })).sort((a, b) => b.averageAmount - a.averageAmount);
    }, [reportData?.amountByClerk]);
    
    useEffect(() => {
        api.get<Record<string, number[]>>('/api/clerk-po-ranges')
            .then(setClerkPoRanges)
            .catch(() => {});
    }, []);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                clerk: filters.selectedClerk,
                startDate: filters.startDate,
                endDate: filters.endDate,
            });
            filters.selectedStatuses.forEach(status => params.append('statuses', status));
            const data = await api.get<OutstandingReportData>(`/api/reports/outstanding-summary?${params.toString()}`);
            setReportData(data);
        } catch (error) {
            setReportData(null);
        } finally {
            setIsLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);
    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (!(event.target instanceof Node)) return;
            if (statusMenuRef.current && !statusMenuRef.current.contains(event.target)) {
                setIsStatusMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    const handleFilterChange = (key: string, value: any) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleStatusChange = (status: string) => {
        setFilters(prev => {
            const newSet = new Set(prev.selectedStatuses);
            if (status === 'new') {
                if (newSet.has('new')) {
                    newSet.delete('new');
                    newSet.delete('viewed');
                } else {
                    newSet.add('new');
                    newSet.add('viewed');
                }
            } else {
                if (newSet.has(status)) {
                    newSet.delete(status);
                } else {
                    newSet.add(status);
                }
            }
            return { ...prev, selectedStatuses: newSet };
        });
    };
    const selectedStatusLabels = statusOptions
        .filter(status => filters.selectedStatuses.has(status.value))
        .map(status => status.label);

    return (
        <div className="reports-container">
            <div className="reports-filter-bar">
                <div className="filter-group">
                    <label htmlFor="start-date">From</label>
                    <input type="date" id="start-date" value={filters.startDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('startDate', e.currentTarget.value)} />
                </div>
                <div className="filter-group">
                    <label htmlFor="end-date">To</label>
                    <input type="date" id="end-date" value={filters.endDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('endDate', e.currentTarget.value)} />
                </div>
                <div className="filter-group">
                    <label htmlFor="clerk-filter">Clerk</label>
                    <select id="clerk-filter" value={filters.selectedClerk} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('selectedClerk', e.currentTarget.value)}>
                        <option value="All">All</option>
                        {clerksForFilter.map(clerk => <option key={clerk} value={clerk}>{clerk}</option>)}
                        {user.permissions?.can_view_unknown_clerk === 'true' && <option value="Unknown">?</option>}
                    </select>
                </div>
                <div className="filter-group filter-popover reports-status-filter" ref={statusMenuRef}>
                    <label>Status</label>
                    <button
                        type="button"
                        className="filter-popover-trigger"
                        onClick={() => setIsStatusMenuOpen(open => !open)}
                    >
                        <span>{selectedStatusLabels.length > 0 ? `${selectedStatusLabels.length} selected` : 'Select statuses'}</span>
                        <span className="filter-caret">{isStatusMenuOpen ? '▴' : '▾'}</span>
                    </button>
                    {isStatusMenuOpen && (
                        <div className="filter-popover-menu">
                            {statusOptions.map(status => (
                                <label key={status.value} className="filter-menu-option">
                                    <input
                                        type="checkbox"
                                        checked={filters.selectedStatuses.has(status.value)}
                                        onChange={() => handleStatusChange(status.value)}
                                    />
                                    <span>{status.label}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
                <button className="btn-primary" onClick={fetchData} disabled={isLoading} style={{alignSelf: 'flex-end', padding: '0.5rem 1rem'}}>
                    {isLoading ? 'Loading...' : 'Generate Report'}
                </button>
            </div>
            {isLoading ? <div className="tracker-message">Generating Report...</div> : (
                <div className="reports-dashboard">
                    {reportData && (
                        <>
                            <div className="kpi-grid">
                                <KpiCard title="Total Outstanding" value={reportData.kpis.totalAmount} formatter={currencyFormatter.format} />
                                <KpiCard title="Invoice Count" value={reportData.kpis.invoiceCount} />
                                <KpiCard title="Average Age (Days)" value={Math.round(reportData.kpis.averageAge)} />
                            </div>
                            <div className="charts-grid">
                                <BarChart
                                    title="Outstanding Count by Status"
                                    data={reportData.amountByStatus}
                                    dataKey="count"
                                    labelKey="status"
                                    formatter={(val) => val.toString()}
                                />
                                <BarChart
                                    title="Outstanding Amount by Status"
                                    data={reportData.amountByStatus}
                                    dataKey="amount"
                                    labelKey="status"
                                    formatter={currencyFormatter.format}
                                />
                                <BarChart
                                    title="Top 10 Vendors by Amount"
                                    data={reportData.topVendors}
                                    dataKey="amount"
                                    labelKey="vendor"
                                    formatter={currencyFormatter.format}
                                />
                                 <BarChart
                                    title="Top 10 Vendors by Invoice Count"
                                    data={reportData.topVendorsByCount}
                                    dataKey="count"
                                    labelKey="vendor"
                                    formatter={(val) => val.toString()}
                                />
                                <BarChart
                                    title="Outstanding Amount by Clerk"
                                    data={reportData.amountByClerk}
                                    dataKey="amount"
                                    labelKey="clerk"
                                    formatter={currencyFormatter.format}
                                />
                                <BarChart
                                    title="Invoice Count by Clerk"
                                    data={reportData.amountByClerk}
                                    dataKey="count"
                                    labelKey="clerk"
                                    formatter={(val) => val.toString()}
                                />
                                <BarChart
                                    title="Average Invoice Amount by Clerk"
                                    data={averageAmountByClerk}
                                    dataKey="averageAmount"
                                    labelKey="clerk"
                                    formatter={currencyFormatter.format}
                                />
                            </div>
                        </>
                    )}
                    {!reportData && <div className="tracker-message">Could not load report data.</div>}
                </div>
            )}
        </div>
    );
};
